import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

/**
 * Renders one language of a consent document as an accordion.
 * Ported from patient_portal/src/components/ConsentDocViewer.jsx (same data
 * shape, CRM palette) so both apps show patients the same document.
 *
 *   doc = { title, sections: [{ summary, open?, intro?, items, note? }] }
 *   item = string | { b?, text?, sub?: string[] }
 */
function Item({ item }) {
  if (typeof item === 'string') return <li className="text-slate-700">{item}</li>
  return (
    <li className="text-slate-700">
      {item.b && <span className="font-semibold text-slate-900">{item.b} </span>}
      {item.text}
      {Array.isArray(item.sub) && item.sub.length > 0 && (
        <ul className="mt-2 ml-1 space-y-1.5 list-[circle] pl-5 marker:text-brand-300">
          {item.sub.map((s, i) => (
            <li key={i} className="text-slate-600 text-[0.95em]">{s}</li>
          ))}
        </ul>
      )}
    </li>
  )
}

function Section({ section }) {
  const [open, setOpen] = useState(!!section.open)
  return (
    <div className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors ${
          open ? 'text-brand-700 border-b border-brand-100' : 'text-slate-900 hover:bg-slate-50'
        }`}
      >
        <span className="font-semibold text-[15px] leading-snug">{section.summary}</span>
        <ChevronDown
          size={18}
          strokeWidth={2.5}
          className={`flex-none transition-transform duration-300 ${open ? 'rotate-180 text-brand-600' : 'text-slate-400'}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-3 text-[14px]">
          {section.intro && <p className="text-slate-700 mb-3 leading-relaxed">{section.intro}</p>}
          <ul className="space-y-3 list-disc pl-5 marker:text-brand-500 leading-relaxed">
            {section.items.map((item, i) => <Item key={i} item={item} />)}
          </ul>
          {section.note && (
            <p className="mt-4 text-[13px] font-medium text-brand-700 bg-brand-50 ring-1 ring-brand-100 rounded-xl px-3 py-2.5">
              {section.note}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default function ConsentDocViewer({ doc }) {
  if (!doc) return null
  return (
    <div className="space-y-3">
      <div className="text-center pt-1 pb-2">
        <h2 className="text-[19px] font-bold text-slate-900 tracking-tight leading-snug">{doc.title}</h2>
        <div className="mx-auto mt-3 h-1 w-12 rounded-full bg-brand-500" />
      </div>
      {doc.sections.map((section, i) => <Section key={i} section={section} />)}
    </div>
  )
}
